// client/src/components/pages/LexiStar/PlanBoard/RoutePanel.jsx
//
// "Routes today" (LS-09 §5.2 right panel): per route its aggregate state, the
// trip counts, a tonnes bar (actual of planned), open deviations and active
// advisories. Routes needing attention sort to the top. Hovering or focusing a
// route highlights it on the map and dims the others; clicking expands its
// trips with stage and progress (links to the trip and its replay).

import React from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaHistory } from 'react-icons/fa';
import { StagePill } from '../StatusBoard/atoms';
import { fmtNum, fmtTime } from '../StatusBoard/format';
import {
  routeStateMeta,
  countsText,
  tonnesProgress,
  truckRegistration,
  truckTone,
} from './planBoardLogic';

export function RouteStatePill({ state }) {
  const meta = routeStateMeta(state);
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${meta.pill}`}
      data-state={state}
    >
      <span aria-hidden="true">{meta.glyph}</span>
      {meta.label}
    </span>
  );
}

function TonnesBar({ tonnes }) {
  const t = tonnesProgress(tonnes);
  if (!t.planned && !t.actual) {
    return <p className="text-xs text-slate-400">No planned tonnage</p>;
  }
  return (
    <div className="flex items-center gap-2">
      <div
        className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"
        role="meter"
        aria-label="Tonnes delivered of planned"
        aria-valuemin={0}
        aria-valuemax={t.planned || 0}
        aria-valuenow={t.actual}
      >
        <div
          className={`h-full rounded-full ${t.over ? 'bg-amber-500' : 'bg-slate-700'}`}
          style={{ width: `${Math.round(t.ratio * 100)}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-slate-700 whitespace-nowrap">
        {fmtNum(t.actual)}/{fmtNum(t.planned)} t
      </span>
    </div>
  );
}

/** A trip on the route (GET /plan-board routes[].trips, else its trucks). */
function TruckRow({ truck }) {
  const pct = Number(truck.progress_pct);
  const hasPct = Number.isFinite(pct);
  const tripId = truck.trip_id || truck.id;
  return (
    <li className="py-2 text-xs space-y-1">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
        <span
          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: truckTone(truck.stage).hex }}
          aria-hidden="true"
        />
        <span className="font-mono font-semibold text-slate-800">{truckRegistration(truck)}</span>
        <StagePill stage={truck.stage} />
        {truck.delayed && (
          <span className="text-orange-800 font-semibold whitespace-nowrap">⏳ delayed</span>
        )}
      </span>
      <span className="flex items-center gap-2 pl-4">
        <span className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <span
            className="block h-full bg-slate-500 rounded-full"
            style={{ width: `${hasPct ? Math.max(0, Math.min(100, pct)) : 0}%` }}
          />
        </span>
        <span className="tabular-nums text-slate-600 whitespace-nowrap">
          {hasPct ? `${Math.round(pct)}%` : '—'}
          {truck.eta ? ` · ETA ${fmtTime(truck.eta)}` : ''}
        </span>
      </span>
      {tripId && (
        <span className="flex items-center gap-2 pl-4">
          <Link
            to={`/lexistar/trips/${tripId}`}
            className="text-primary-700 hover:underline font-medium"
          >
            {truck.reference || 'Trip'}
          </Link>
          <Link
            to={`/lexistar/replay?trip_id=${encodeURIComponent(tripId)}`}
            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"
            aria-label={`Replay ${truckRegistration(truck)}`}
            title="Replay"
          >
            <FaHistory size={10} aria-hidden="true" />
            <span aria-hidden="true">Replay</span>
          </Link>
        </span>
      )}
    </li>
  );
}

export default function RoutePanel({ routes, expandedId, onToggle, onFocus, compact = false }) {
  if (!routes.length) {
    return (
      <p className="p-5 text-sm text-slate-500">
        No routes planned for this day with these filters. Plan trips on{' '}
        <Link to="/lexistar/plan" className="text-primary-700 hover:underline">
          Plan &amp; Dispatch
        </Link>
        .
      </p>
    );
  }
  return (
    <ul className="divide-y divide-slate-100" aria-label="Routes today">
      {routes.map((r) => {
        const id = String(r.route_id);
        const open = expandedId === id;
        const advisories = r.advisories || [];
        const deviations = Number(r.open_deviations) || 0;
        return (
          <li
            key={id}
            onMouseEnter={() => onFocus(id)}
            onMouseLeave={() => onFocus(null)}
            data-route={id}
          >
            <button
              type="button"
              onClick={() => onToggle(id)}
              onFocus={() => onFocus(id)}
              onBlur={() => onFocus(null)}
              aria-expanded={open}
              className={`w-full text-left px-4 ${compact ? 'py-2' : 'py-3'} hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50`}
            >
              <span className="flex items-start gap-2">
                <span className="mt-1 text-slate-400">
                  {open ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </span>
                <span className="flex-1 min-w-0 space-y-1.5">
                  {/* Code and state on one row, the name on its own line, so a
                      narrow panel wraps the name between words. */}
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-slate-500 whitespace-nowrap">
                      {r.code || ''}
                    </span>
                    <span className="flex-shrink-0">
                      <RouteStatePill state={r.state} />
                    </span>
                  </span>
                  <span className="block font-semibold text-sm text-slate-900">{r.name}</span>
                  {r.lane && !compact && (
                    <span className="block text-[11px] text-slate-500 truncate">
                      {typeof r.lane === 'object' ? r.lane.name || r.lane.code : r.lane}
                    </span>
                  )}
                  <span className="block text-xs text-slate-600 tabular-nums">
                    {countsText(r.counts)}
                  </span>
                  <TonnesBar tonnes={r.tonnes} />
                  {(deviations > 0 || advisories.length > 0) && (
                    <span className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs font-medium">
                      {deviations > 0 && (
                        <span className="text-pink-800">
                          <span aria-hidden="true">⤴ </span>
                          {deviations} deviation{deviations === 1 ? '' : 's'} (open)
                        </span>
                      )}
                      {advisories.length > 0 && (
                        <span className="text-amber-800">
                          <span aria-hidden="true">⚠ </span>
                          {advisories.length} advisor{advisories.length === 1 ? 'y' : 'ies'}
                        </span>
                      )}
                    </span>
                  )}
                </span>
              </span>
            </button>
            {open && (
              <div className="px-4 pb-3 pl-9">
                {(r.trips?.length ? r.trips : r.trucks || []).length === 0 ? (
                  <p className="text-xs text-slate-500">No trips on this route yet today.</p>
                ) : (
                  <ul aria-label={`Trips on ${r.name}`} className="divide-y divide-slate-50">
                    {(r.trips?.length ? r.trips : r.trucks).map((t) => (
                      <TruckRow key={t.id || t.trip_id || t.asset_key} truck={t} />
                    ))}
                  </ul>
                )}
                {deviations > 0 && (
                  <Link
                    to="/lexistar/approvals"
                    className="inline-block mt-2 text-xs font-medium text-primary-700 hover:underline"
                  >
                    Open approvals
                  </Link>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
